import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
  type ReactNode,
} from 'react';
import { languageOptions, resources, type LanguageCode } from '../locales';
import { detectPathLanguage } from '../i18n/urlLanguage';
import skoletoonsResources from '../locales/skoletoons';

type Language = LanguageCode;
type BaseLanguage = 'fr' | 'en';

const SUPPORTED_LANGUAGE_CODES = new Set<string>(languageOptions.map((option) => option.code));

function getBaseLanguage(language: Language): BaseLanguage {
  return language === 'en' ? 'en' : 'fr';
}

type TranslationTree = {
  [key: string]: string | TranslationTree;
};

async function loadOverlay(code: Language): Promise<Partial<TranslationTree>> {
  try {
    // Importe dynamiquement le fichier d'overlay basé sur la langue
    const mod = await import(`../locales/overlays/${code}.ts`);
    // L'export peut être `overlay` ou `overlayFr`, `overlayEn`, etc.
    return (mod.overlay ??
      mod[`overlay${code.charAt(0).toUpperCase()}${code.slice(1)}`]) as Partial<TranslationTree>;
  } catch (e) {
    console.error(`Failed to load overlay for language: ${code}`, e);
    return {};
  }
}

const translations: Record<BaseLanguage, TranslationTree> = {
  fr: {
    common: {
      search: 'Rechercher',
      loadMore: 'Voir plus',
      open: 'Ouvrir',
      send: 'Envoyer',
      close: 'Fermer',
      explore: 'Explorer',
      play: 'Lire',
      pause: 'Pause',
      paused: 'En pause',
      hide: 'Masquer',
    },
    header: {
      universe: 'Skoletoon Universe',
      nav: {
        home: 'Accueil',
        collection: 'Collection',
        wardrobe: 'Garde-robe',
        music: 'Musique',
        subscriptions: 'Abonnements',
        news: 'Actualités',
        help: 'Aide',
      },
      user: {
        dashboard: 'Mon Dashboard',
        orders: 'Mes commandes',
        favorites: 'Favoris',
        admin: 'Console admin',
        logout: 'Déconnexion',
      },
    },
    home: {
      heroTitle1: "Entrez dans l'univers",
      heroTitle2: "SkoleToon's",
      heroDescription:
        'Une marque née entre street culture, luxe contemporain et esthétique futuriste.',
      downloadExtension: "Télécharger l'extension",
      playVideo: 'Lire la vidéo',
      presentedBy: 'Skoleom présente',
      shopCta: 'Voir la boutique',
      newsCta: "Voir l'actualité",
      extensionCta: 'Extension',
      downloadPickerTitle: 'Télécharger Skoleom SeSync',
      downloadPickerText: "Choisissez votre navigateur pour installer l'extension.",
      downloadFor: 'Télécharger pour',
      openInNewTab: 'nouvel onglet',
      unavailableRegion: 'indisponible dans votre région',
      accessTo: 'Accéder à',
      immersiveA: 'Chaque vidéo devient un véritable magasin immersif.',
      immersiveB: 'Une expérience fluide, élégante et interactive.',
      wearFuture: 'WEAR THE FUTURE',
      latestCollections: 'Dernières collections',
      latestCollectionsSubtitle: "Des silhouettes pensées pour aujourd'hui. Conçues pour demain.",
      collectionsTitle: 'Collections',
      allSee: 'Tout voir',
      shopLabel: 'Boutique',
      newProductsTitle: 'Nouveautés',
      editLabel: 'Inspiration',
      editTitle: 'Edit',
      newsHeroTitle: 'Actualités',
      newsVideoTitle: "Actualités SkoleToon's",
      newsPageAria: 'Voir les actualités',
      newsHeroMeta: 'News / Drops / Culture',
      newsArticleCta: "Voir l'article",
      allCollection: 'Voir toute la collection',
      carouselPrev: 'Collection precedente',
      carouselNext: 'Collection suivante',
      carouselRegion: 'Carrousel des dernieres collections',
      seePiece: 'Voir la pièce',
      nowPlaying: 'écouter maintenant',
      listenExtract: 'Écouter un extrait',
      downloadTrack: 'Télécharger le son',
      touchTagline: 'Regarde. Touche. Achète. Interface mobile immersive.',
      listenSound: 'Écouter le son',
      choosePlatform: 'Choisis ta plateforme',
      download: 'Télécharger',
      musiqueDescription: "Un son, un vibe. Tout l'univers de SkoleToon's.",
      musicVisualAlt: "Visuel musique SkoleToon's",
      musicVisualBadge: 'Visual / Photos',
      liveSpectrum: 'Visualisation audio en temps réel',
      seekTrack: 'Naviguer dans le son',
      restartTrack: 'Rejouer',
      newTitleA: 'Actualités',
      newTitleB: 'Salle de presse',
      newDescription:
        'Suivi des avancées Skoletoon et Skoleom : produit, partenariats, expansion et technologie.',
      newButton: 'Voir toutes les actualités',
      newReadMore: 'Lire sur la page actualités',
      newsCards: {
        innovation: {
          category: 'Innovation',
          title: 'Skoletoon active le mode shopping en direct sur ses contenus vidéo',
          excerpt:
            "Les capsules interactives permettent d'ouvrir la pièce vue à l'écran en un clic, sans quitter la lecture.",
        },
        partnership: {
          category: 'Partenariat',
          title: 'Nouveau partenariat créateurs pour accélérer les drops Skoletoon',
          excerpt:
            'Skoletoon collabore avec des studios et talents mode pour lancer des collections capsule plus fréquentes.',
        },
        expansion: {
          category: 'Expansion',
          title: 'Skoletoon étend son univers sur de nouveaux marchés européens',
          excerpt:
            'Le déploiement inclut de nouvelles expériences locales, des contenus adaptés et des activations communautaires.',
        },
      },
    },
    stores: {
      badge: 'Shop',
      titleA: 'La',
      titleB: 'Boutique',
      description:
        'Découvrez une sélection de tenues & collections sport : pièces Bloc 90, survêtements, accessoires, capsules exclusives.',
      seeSelection: 'Voir la sélection',
      discoverDrop: 'Découvrir le drop',
      featuredLook: 'Look a la une',
      imageReserved: 'Bloc visuel réservé aux images de la maquette.',
      comingSoon: 'À venir',
      comingSoonText:
        'Un espace éditorial pour présenter ce qui arrive ensuite dans la collection.',
      notifyMe: 'Prévenir moi',
      featured: 'À la une',
      preview: 'Bloc preview',
      catalog: 'Catalogue',
      catalogText: 'Sélection courte et dense',
      openCatalog: 'Voir le catalogue',
      seePiece: 'Voir la pièce',
      editorialBanner: 'Bannière éditoriale',
      navigation: 'Navigation',
      removeFavorite: 'Retirer des favoris',
      addFavorite: 'Ajouter aux favoris',
      viewPhoto: 'Voir la photo',
      newBadge: 'Nouveau',
      iconic: {
        arBadge: 'Essayage AR disponible',
        iconicBadge: 'Collection iconique',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'La collection signature inspirée du sport automobile et de la performance. Pièces Bloc 90, vestes reverso et accessoires checker — essayage en réalité augmentée, achat en un clic.',
        cta: 'Découvrir la collection',
      },
      contest: {
        title: 'Tentez de gagner des t-shirts',
        text: 'Plus que quelques membres avant le lancement du concours ! À 5000 inscrits, 50 maillots officiels seront mis en jeu.',
        cta: 'Participer maintenant',
        note: "* Offre valable jusqu'à août 2026.",
      },
    },
    news: {
      badge: 'Salle de presse Skoletoon x Skoleom',
      titleA: 'Actualités',
      titleB: "SkoleToon's & Skoleom",
      description:
        'Toutes les annonces officielles autour des avancées produit, partenariats et déploiements de l écosystème Skoletoon et Skoleom.',
      featured: 'À la une',
      readArticle: "Lire l'article",
      stayInformed: 'Reste informé',
      subscribe: "S'abonner",
      nextAnnouncement: 'Prochaine annonce',
      nextAnnouncementText: 'Nouveau drop plateforme en préparation',
      nextAnnouncementSub: 'Mise à jour prévue la semaine prochaine.',
      publishedOn: 'Publié le',
      shopCta: 'Voir la boutique',
      newsCta: "Voir l'actualité",
    },
    help: {
      badge: "Centre d'aide Skoletoon",
      titleA: "Besoin d'aide ?",
      titleB: 'On est là.',
      description:
        'Trouve rapidement la réponse à tes questions sur les commandes, la lecture des contenus et ton compte Skoletoon.',
      quickSearch: 'Recherche rapide',
      quickSearchPlaceholder: 'Ex: suivi commande, lecture vidéo, remboursement...',
      search: 'Rechercher',
      support: 'Support',
      faq: 'FAQ Populaire',
      contactSupport: 'Contacter le support',
      supportHours: 'Notre équipe répond du lundi au samedi, de 9h à 19h.',
      fullName: 'Nom complet',
      email: 'Email',
      subject: 'Sujet',
      message: 'Message',
      helpOpen: 'Ouvrir',
      supportEmail: 'Email',
      hq: 'Siège',
    },
    music: {
      titleA: 'UNE SEULE MUSIQUE,',
      titleB: 'UN UNIVERS',
      description: "They see us now n'est pas juste un son. C'est l'énergie d'un mouvement.",
      nowPlaying: 'En cours de lecture',
      listen: 'Écouter le son',
      download: 'Télécharger le son',
      immersive: 'EXPÉRIENCE IMMERSIVE',
      immersiveSub: 'Vis ton style en rythme avec la musique.',
      looks: 'LOOKS INSPIRÉS PAR LE SON',
      looksSub: "Découvre des tenues créées pour vibrer avec l'ambiance du son.",
      seePiece: 'Voir la pièce',
      playlistSoon: 'Une nouvelle playlist arrive bientôt',
      playlistSoonSub: "Reste connecté pour découvrir les prochaines vibes de SkoleToon's.",
      alertMe: 'Être alerté',
    },
  },
  en: {
    common: {
      search: 'Search',
      loadMore: 'See more',
      open: 'Open',
      send: 'Send',
      close: 'Close',
      explore: 'Explore',
      play: 'Play',
      pause: 'Pause',
      paused: 'Paused',
      hide: 'Hide',
    },
    header: {
      universe: 'Skoletoon Universe',
      nav: {
        home: 'Home',
        collection: 'Collection',
        wardrobe: 'Wardrobe',
        music: 'Music',
        subscriptions: 'Subscriptions',
        news: 'News',
        help: 'Help',
      },
      user: {
        dashboard: 'My Dashboard',
        orders: 'My orders',
        favorites: 'Favorites',
        admin: 'Admin Console',
        logout: 'Sign out',
      },
    },
    home: {
      heroTitle1: 'Enter the world of',
      heroTitle2: "SkoleToon's",
      heroDescription:
        'A brand born between street culture, contemporary luxury and futuristic aesthetics.',
      downloadExtension: 'Download extension',
      playVideo: 'Play video',
      presentedBy: 'Skoleom presents',
      shopCta: 'View shop',
      newsCta: 'View news',
      extensionCta: 'Extension',
      downloadPickerTitle: 'Download Skoleom SeSync',
      downloadPickerText: 'Choose your browser to install the extension.',
      downloadFor: 'Download for',
      openInNewTab: 'new tab',
      unavailableRegion: 'unavailable in your region',
      accessTo: 'Go to',
      immersiveA: 'Every video becomes a truly immersive store.',
      immersiveB: 'A fluid, elegant and interactive experience.',
      wearFuture: 'WEAR THE FUTURE',
      latestCollections: 'Latest collections',
      latestCollectionsSubtitle: 'Silhouettes made for today. Built for tomorrow.',
      collectionsTitle: 'Collections',
      allSee: 'View all',
      shopLabel: 'Shop',
      newProductsTitle: 'New arrivals',
      editLabel: 'Inspiration',
      editTitle: 'Edit',
      newsHeroTitle: 'News',
      newsVideoTitle: "SkoleToon's News",
      newsPageAria: 'View news',
      newsHeroMeta: 'News / Drops / Culture',
      newsArticleCta: 'View article',
      allCollection: 'View full collection',
      carouselPrev: 'Previous collection',
      carouselNext: 'Next collection',
      carouselRegion: 'Latest collections carousel',
      seePiece: 'View item',
      nowPlaying: 'Now playing',
      listenExtract: 'Listen to preview',
      downloadTrack: 'Download track',
      touchTagline: 'Watch. Touch. Buy. Immersive mobile interface.',
      listenSound: 'Listen to the track',
      choosePlatform: 'Choose your platform',
      download: 'Download',
      musiqueDescription: "A track, a vibe. The whole universe of SkoleToon's.",
      musicVisualAlt: "SkoleToon's music visual",
      musicVisualBadge: 'Visual / Photos',
      liveSpectrum: 'Live audio visualization',
      seekTrack: 'Seek track',
      restartTrack: 'Replay',
      newTitleA: 'News',
      newTitleB: 'Newsroom',
      newDescription:
        'Track Skoletoon and Skoleom progress across product, partnerships, expansion and technology.',
      newButton: 'See all news',
      newReadMore: 'Read on the news page',
      newsCards: {
        innovation: {
          category: 'Innovation',
          title: 'Skoletoon enables live shopping across its video content',
          excerpt:
            'Interactive capsules now let viewers open the exact featured item in one click, without leaving playback.',
        },
        partnership: {
          category: 'Partnership',
          title: 'New creator partnership accelerates Skoletoon drops',
          excerpt:
            'Skoletoon teams up with fashion studios and talents to launch capsule collections more frequently.',
        },
        expansion: {
          category: 'Expansion',
          title: 'Skoletoon expands its universe into new European markets',
          excerpt:
            'The rollout adds localized experiences, adapted content, and stronger community activations.',
        },
      },
    },
    stores: {
      badge: 'Shop',
      titleA: 'The',
      titleB: 'Store',
      description:
        'Discover a selection of sport outfits & collections: jerseys, tracksuits, accessories, exclusive capsules.',
      seeSelection: 'View selection',
      discoverDrop: 'Discover drop',
      featuredLook: 'Featured look',
      imageReserved: 'Visual block reserved for mockup assets.',
      comingSoon: 'Coming soon',
      comingSoonText: 'An editorial space to preview what drops next.',
      notifyMe: 'Notify me',
      featured: 'Featured',
      preview: 'Preview block',
      catalog: 'Catalog',
      catalogText: 'Short and focused selection',
      openCatalog: 'Open catalog',
      seePiece: 'View item',
      editorialBanner: 'Editorial banner',
      navigation: 'Navigation',
      removeFavorite: 'Remove from favorites',
      addFavorite: 'Add to favorites',
      viewPhoto: 'View photo',
      newBadge: 'New',
      iconic: {
        arBadge: 'AR try-on available',
        iconicBadge: 'Iconic collection',
        titleLine1: 'On the road',
        titleLine2: 'to success',
        brand: "— SkoleToon's",
        description:
          'The signature collection inspired by motorsport and performance. Jerseys, reversible jackets and checker accessories — augmented reality try-on, one-click purchase.',
        cta: 'Discover the collection',
      },
      contest: {
        title: 'Win a t-shirt',
        text: 'Just a few more members before the contest launches! At 5000 sign-ups, 50 official jerseys will be up for grabs.',
        cta: 'Join now',
        note: '* Offer valid until August 2026.',
      },
    },
    news: {
      badge: 'Skoletoon x Skoleom Newsroom',
      titleA: 'News',
      titleB: "SkoleToon's & Skoleom",
      description:
        'All official announcements about product progress, partnerships and ecosystem rollouts across Skoletoon and Skoleom.',
      featured: 'Featured',
      readArticle: 'Read article',
      stayInformed: 'Stay informed',
      subscribe: 'Subscribe',
      nextAnnouncement: 'Next announcement',
      nextAnnouncementText: 'New platform drop in preparation',
      nextAnnouncementSub: 'Update planned next week.',
      publishedOn: 'Published on',
      shopCta: 'View shop',
      newsCta: 'View news',
    },
    help: {
      badge: 'Skoletoon Help Center',
      titleA: 'Need help?',
      titleB: 'We are here.',
      description:
        'Find answers quickly about orders, content playback and your Skoletoon account.',
      quickSearch: 'Quick search',
      quickSearchPlaceholder: 'Ex: order tracking, video playback, refund...',
      search: 'Search',
      support: 'Support',
      faq: 'Popular FAQ',
      contactSupport: 'Contact support',
      supportHours: 'Our team replies Monday to Saturday, 9am to 7pm.',
      fullName: 'Full name',
      email: 'Email',
      subject: 'Subject',
      message: 'Message',
      helpOpen: 'Open',
      supportEmail: 'Email',
      hq: 'Headquarters',
    },
    music: {
      titleA: 'ONE MUSIC,',
      titleB: 'ONE UNIVERSE',
      description: "They see us now is not just a song. It's the energy of a movement.",
      nowPlaying: 'Now playing',
      listen: 'Listen to track',
      download: 'Download track',
      immersive: 'IMMERSIVE EXPERIENCE',
      immersiveSub: 'Live your style in sync with the music.',
      looks: 'LOOKS INSPIRED BY SOUND',
      looksSub: 'Discover outfits designed to match the vibe of the sound.',
      seePiece: 'View item',
      playlistSoon: 'A new playlist is coming soon',
      playlistSoonSub: "Stay connected for the next SkoleToon's vibes.",
      alertMe: 'Notify me',
    },
  },
};

type I18nContextValue = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  geoLangDetected: boolean;
  isChangingLanguage: boolean;
};

const LanguageContext = createContext<I18nContextValue | null>(null);

function normalizeLanguage(value: string | null | undefined): Language {
  if (!value) return 'fr';
  const normalized = value.toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGE_CODES.has(normalized) ? (normalized as Language) : 'fr';
}

function resolvePath(tree: TranslationTree, path: string): string | undefined {
  const parts = path.split('.');
  let cursor: string | TranslationTree | undefined = tree;

  for (const part of parts) {
    if (!cursor || typeof cursor === 'string') return undefined;
    cursor = cursor[part];
  }

  return typeof cursor === 'string' ? cursor : undefined;
}

function isTranslationTree(value: string | TranslationTree | undefined): value is TranslationTree {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge(target: TranslationTree, source: Partial<TranslationTree>): TranslationTree {
  const output: TranslationTree = { ...target };
  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = target[key];
    if (isTranslationTree(sourceValue) && isTranslationTree(targetValue)) {
      output[key] = deepMerge(targetValue, sourceValue);
    } else if (sourceValue !== undefined) {
      output[key] = sourceValue;
    }
  });
  return output;
}

function asTranslationTree(value: unknown): TranslationTree {
  return isTranslationTree(value as string | TranslationTree | undefined)
    ? (value as TranslationTree)
    : {};
}

export function LanguageProvider({
  children,
  initialLanguage = 'fr',
}: {
  children: ReactNode;
  initialLanguage?: Language;
}) {
  const [isPending, startTransition] = useTransition();
  const [language, setLanguageState] = useState<Language>(() => normalizeLanguage(initialLanguage));
  const [geoLangDetected, setGeoLangDetected] = useState(false);
  const [overlay, setOverlay] = useState<Partial<TranslationTree>>({});

  useEffect(() => {
    const fromPath = detectPathLanguage(window.location.pathname);
    if (fromPath) {
      setLanguageState(fromPath);
      setGeoLangDetected(false);
      return;
    }
    const saved =
      window.localStorage.getItem('skoleom-language') ??
      window.localStorage.getItem('Skoletoon.lang') ??
      window.localStorage.getItem('sskoleom-language');
    if (saved) {
      setLanguageState(normalizeLanguage(saved));
      setGeoLangDetected(false);
    } else {
      // Keep initial route language to avoid SSR/CSR hydration mismatches.
      setGeoLangDetected(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    startTransition(() => {
      loadOverlay(language).then((nextOverlay) => {
        if (!cancelled) {
          setOverlay(nextOverlay);
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    startTransition(() => {
      setLanguageState(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('Skoletoon.lang', next);
        window.localStorage.setItem('skoleom-language', next);
        window.localStorage.setItem('sskoleom-language', next);
      }
    });
  }, []);

  const baseLanguage = getBaseLanguage(language);

  const mergedTranslations = useMemo(() => {
    const localeResource = asTranslationTree(resources[language]?.translation);
    const skoletoonsResource = asTranslationTree(skoletoonsResources[language]);
    return deepMerge(
      deepMerge(deepMerge(translations[baseLanguage], localeResource), skoletoonsResource),
      overlay,
    );
  }, [baseLanguage, language, overlay]);

  const t = useCallback(
    (key: string) => {
      const primary = resolvePath(mergedTranslations, key);
      // Keep current language only to avoid FR UI unexpectedly showing EN text.
      return primary ?? resolvePath(translations[baseLanguage], key) ?? key;
    },
    [mergedTranslations, baseLanguage],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      geoLangDetected,
      isChangingLanguage: isPending,
    }),
    [language, setLanguage, t, geoLangDetected, isPending],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useI18n must be used inside LanguageProvider');
  return context;
}
